import { useTranslation } from 'react-i18next';
import type { ErrorCatalogResponse, ErrorCodeNode, ErrorGroup } from '../types/errorDemo';

/**
 * #626 (R7-087): 'ko_KR' 류 raw locale 코드를 친화 언어명으로 변환.
 * Intl.DisplayNames 미지원/미등록 코드는 raw 코드 그대로 반환 (fail-safe).
 */
function formatLocaleLabel(localeCode: string): string {
  try {
    const bcp47 = localeCode.replace('_', '-');
    const label = new Intl.DisplayNames([bcp47], { type: 'language' }).of(bcp47);
    return label ?? localeCode;
  } catch {
    return localeCode;
  }
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

export function CatalogSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      aria-busy="true"
      aria-label={t('errorDemo.catalog.title')}
      style={{ padding: 'var(--spacing-4)' }}
    >
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 20,
            background: 'var(--color-surface-elevated)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 'var(--spacing-3)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

// ─── Group colors ─────────────────────────────────────────────────────────────

function prefixColor(prefix: string): string {
  switch (prefix.toUpperCase()) {
    case 'COMMON':     return 'var(--color-neutral)';
    case 'AUTH':       return 'var(--color-warning)';
    case 'VALIDATION': return 'var(--color-info)';
    case 'USER':       return 'var(--color-brand-primary)';
    default:           return 'var(--color-text-muted)';
  }
}

// ─── NodeItem ─────────────────────────────────────────────────────────────────

interface NodeItemProps {
  node: ErrorCodeNode;
  isSelected: boolean;
  onSelect: (node: ErrorCodeNode) => void;
}

function NodeItem({ node, isSelected, onSelect }: NodeItemProps) {
  return (
    <li
      role="treeitem"
      aria-selected={isSelected}
      aria-label={`${node.code} ${node.messageKo}`}
      tabIndex={0}
      onClick={() => onSelect(node)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(node);
        }
      }}
      style={{
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--spacing-2) var(--spacing-3) var(--spacing-2) var(--spacing-6)',
        cursor: 'pointer',
        borderLeft: `2px solid ${isSelected ? 'var(--color-brand-primary)' : 'transparent'}`,
        background: isSelected
          ? 'color-mix(in srgb, var(--color-brand-primary) 10%, transparent)'
          : 'transparent',
        borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
        outline: 'none',
        transition: 'background 0.15s',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontFamily: 'var(--font-family-mono)',
          color: isSelected ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
          fontWeight: isSelected ? 700 : 400,
        }}
      >
        {node.code}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          marginTop: 1,
        }}
      >
        {node.messageKo}
      </span>
    </li>
  );
}

// ─── GroupSection ─────────────────────────────────────────────────────────────

interface GroupSectionProps {
  group: ErrorGroup;
  isExpanded: boolean;
  selectedCode: string | null;
  onToggle: (prefix: string) => void;
  onSelect: (node: ErrorCodeNode) => void;
}

function GroupSection({ group, isExpanded, selectedCode, onToggle, onSelect }: GroupSectionProps) {
  const { t } = useTranslation();
  const color = prefixColor(group.prefix);

  return (
    <li
      role="treeitem"
      aria-expanded={isExpanded}
      aria-label={`${group.prefix} (${group.nodes.length})`}
      style={{ listStyle: 'none' }}
    >
      {/* Group header */}
      <button
        type="button"
        onClick={() => onToggle(group.prefix)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-3)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          textAlign: 'left',
          outline: 'none',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(group.prefix);
          }
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            transition: 'transform 0.15s',
            display: 'inline-block',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          ▶
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            fontFamily: 'var(--font-family-mono)',
            color,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {group.prefix}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            marginLeft: 'var(--spacing-1)',
          }}
        >
          ({group.nodes.length})
        </span>
      </button>

      {/* Child nodes */}
      {isExpanded && (
        <ul
          role="group"
          aria-label={`${group.prefix} ${t('errorDemo.catalog.title')}`}
          style={{ padding: 0, margin: 0 }}
        >
          {group.nodes.map((node) => (
            <NodeItem
              key={node.code}
              node={node}
              isSelected={selectedCode === node.code}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── ErrorCodeCatalogTree ─────────────────────────────────────────────────────

interface ErrorCodeCatalogTreeProps {
  catalog: ErrorCatalogResponse;
  selectedCode: string | null;
  expandedGroups: Set<string>;
  onToggleGroup: (prefix: string) => void;
  onSelectNode: (node: ErrorCodeNode) => void;
}

/**
 * Left panel — ErrorCode catalog tree.
 * Renders 4 collapsible groups: COMMON / AUTH / VALIDATION / USER.
 */
export function ErrorCodeCatalogTree({
  catalog,
  selectedCode,
  expandedGroups,
  onToggleGroup,
  onSelectNode,
}: ErrorCodeCatalogTreeProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Tree header */}
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-surface-border)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}
      >
        {t('errorDemo.catalog.title')}
      </div>

      {/* Tree body */}
      <ul
        role="tree"
        aria-label={t('errorDemo.catalog.title')}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--spacing-2) 0',
          margin: 0,
        }}
      >
        {catalog.groups.map((group) => (
          <GroupSection
            key={group.prefix}
            group={group}
            isExpanded={expandedGroups.has(group.prefix)}
            selectedCode={selectedCode}
            onToggle={onToggleGroup}
            onSelect={onSelectNode}
          />
        ))}
      </ul>

      {/* Footer — trace / locale info */}
      <div
        style={{
          padding: 'var(--spacing-2) var(--spacing-3)',
          borderTop: '1px solid var(--color-surface-border)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-family-mono)',
          background: 'var(--color-surface-muted)',
        }}
      >
        {/* #626 (R7-087): raw locale 코드 단독 노출 대신 친화 라벨 + 코드 병기 (학습용 디버그 정보 유지) */}
        <div>
          {t('errorDemo.catalog.footer.locale')}: {formatLocaleLabel(catalog.currentLocale)} ({catalog.currentLocale})
        </div>
        <div
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={catalog.currentTraceId}
        >
          {t('errorDemo.catalog.footer.traceId')}: {catalog.currentTraceId.slice(0, 12)}…
        </div>
      </div>
    </div>
  );
}
